import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSesion } from '../estado/SesionContext';
import type { Intento, ResumenCurso } from '../domain/types';

/**
 * Las transversales van fijas; la de cada curso sale del propio curso.
 *
 * Estaban las cuatro escritas a mano, y eso significaba que un curso nuevo repartia una
 * insignia que despues no se mostraba en ningun lado: `listarInsignias` devolvia su slug y
 * este panel no lo conocia. Se ganaba algo invisible.
 */
const INSIGNIAS_FIJAS = [
  { codigo: 'arranque', ico: '🚀', nombre: 'Arranque' },
  { codigo: 'nota_perfecta', ico: '💯', nombre: 'Nota Perfecta' },
];

function insigniasDeLaRuta(cursos: ResumenCurso[]) {
  const porCurso = cursos
    .filter((r) => r.curso.estado === 'publicado')
    .map((r) => ({
      codigo: r.curso.slug,
      ico: r.curso.insigniaIco,
      nombre: r.curso.insigniaNombre,
    }));
  return [INSIGNIAS_FIJAS[0], ...porCurso, INSIGNIAS_FIJAS[1]];
}

function fechaLarga(): string {
  // America/Lima: la app se usa en Perú y la fecha del saludo tiene que ser la de allá.
  return new Date().toLocaleDateString('es-PE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Lima',
  });
}

export function Home() {
  const { perfil, repo, espacio, puntos } = useSesion();
  const [resumen, setResumen] = useState<ResumenCurso[] | null>(null);
  const [insignias, setInsignias] = useState<string[]>([]);
  const [intentos, setIntentos] = useState<Map<string, Intento[]>>(new Map());

  useEffect(() => {
    if (!repo) return;
    let vivo = true;

    void (async () => {
      const [filas, codigos] = await Promise.all([
        repo.listarResumen(espacio.id),
        repo.listarInsignias(),
      ]);
      if (!vivo) return;
      setResumen(filas);
      setInsignias(codigos);

      // Solo de los cursos que la persona empezó: no tiene sentido pedir el historial de
      // un curso que nunca tocó.
      const tocados = filas.filter((f) => f.inscripcion || f.mejorNota !== null);
      const historial = await Promise.all(
        tocados.map(async (f) => [f.curso.id, await repo.listarIntentos(f.curso.id)] as const),
      );
      if (vivo) setIntentos(new Map(historial));
    })();

    return () => {
      vivo = false;
    };
  }, [repo, espacio.id]);

  const nombreCorto = perfil?.nombres.trim().split(' ')[0] ?? '';
  const disponibles = resumen?.filter((r) => r.curso.estado === 'publicado') ?? [];
  const aprobados = disponibles.filter((r) => r.aprobado).length;
  const totalRuta = resumen?.length ?? 0;
  const pct = totalRuta === 0 ? 0 : Math.round((100 * aprobados) / totalRuta);

  const notas = disponibles.map((r) => r.mejorNota).filter((n): n is number => n !== null);
  const promedio = notas.length ? Math.round(notas.reduce((a, b) => a + b, 0) / notas.length) : null;

  const conHistorial = disponibles.filter((r) => (intentos.get(r.curso.id)?.length ?? 0) > 0);

  const circunferencia = 2 * Math.PI * 38;
  const rolLegible = perfil?.roles.includes('coordinador') ? 'Coordinador' : 'Impulso';

  return (
    <div>
      {/* ---------- saludo ---------- */}
      <header className="mb-6 flex flex-wrap items-end justify-between gap-2">
        <h1 className="font-titulos text-3xl font-black leading-tight sm:text-[2.1rem]">
          Hola, <span className="text-rojo">{nombreCorto}</span> 👋
        </h1>
        <p className="text-sm font-semibold text-gris">
          {rolLegible} · {espacio.cliente} &nbsp;|&nbsp; {fechaLarga()}
        </p>
      </header>

      {/* ---------- KPIs ---------- */}
      <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <Kpi
          valor={`${aprobados}/${disponibles.length}`}
          etiqueta="Módulos aprobados"
          color="var(--azul)"
        />
        <Kpi
          valor={puntos}
          etiqueta="Puntos acumulados"
          color="var(--oro)"
          extra={puntos > 0 ? '▲ listos para canjear' : undefined}
        />
        <Kpi valor={promedio ?? '—'} etiqueta="Nota promedio" color="var(--rojo)" />
        <Kpi valor={insignias.length} etiqueta="Insignias ganadas" color="var(--verde)" />
      </section>

      {/* ---------- ruta de certificación ---------- */}
      <section className="mb-6 flex flex-wrap items-center gap-6 rounded-2xl bg-white p-6 shadow-[0_10px_30px_rgba(30,63,148,.08)]">
        <div className="relative h-[92px] w-[92px] shrink-0">
          <svg width="92" height="92" className="-rotate-90">
            <circle cx="46" cy="46" r="38" stroke="var(--borde)" strokeWidth="9" fill="none" />
            <circle
              cx="46"
              cy="46"
              r="38"
              stroke="var(--rojo)"
              strokeWidth="9"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circunferencia}
              strokeDashoffset={circunferencia * (1 - pct / 100)}
              style={{ transition: 'stroke-dashoffset 1s' }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center font-titulos text-lg font-black">
            {pct}%
          </span>
        </div>

        <div className="min-w-[230px] flex-1">
          <h2 className="font-titulos text-lg font-extrabold">Tu ruta de certificación</h2>
          <p className="mt-1 text-sm leading-relaxed text-gris">
            {aprobados === 0
              ? `Aún no apruebas módulos. Empieza por ${disponibles[0]?.curso.titulo ?? 'el primer módulo'} — ${disponibles[0]?.curso.duracionMin ?? 15} minutos y ganas tus primeros puntos.`
              : aprobados < disponibles.length
                ? `¡Buen avance! Te falta aprobar ${disponibles.length - aprobados} módulo disponible para completar tu ruta actual.`
                : '¡Completaste todos los módulos disponibles! Atenta a los próximos cursos.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {insigniasDeLaRuta(resumen ?? []).map((i) => {
            const ganada = insignias.includes(i.codigo);
            return (
              <span
                key={i.codigo}
                title={ganada ? `${i.nombre} — ganada` : `${i.nombre} — aún no`}
                className={`flex items-center gap-1.5 rounded-full border-[1.5px] px-3 py-1.5 text-xs font-extrabold ${
                  ganada
                    ? 'border-oro bg-oro/10 text-tinta'
                    : 'border-borde bg-white text-gris opacity-55 grayscale'
                }`}
              >
                <span className="text-base leading-none">{i.ico}</span>
                {i.nombre}
              </span>
            );
          })}
        </div>
      </section>

      {/* ---------- historial ---------- */}
      {conHistorial.length > 0 && (
        <section className="mb-6 rounded-2xl bg-white p-6 shadow-[0_10px_30px_rgba(30,63,148,.08)]">
          <h2 className="mb-2 font-titulos text-lg font-extrabold">📋 Lo que llevas hasta ahora</h2>
          {conHistorial.map((r) => {
            const suyos = intentos.get(r.curso.id) ?? [];
            const n = suyos.length;
            return (
              <div
                key={r.curso.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-borde py-2.5 text-sm last:border-0"
              >
                <span>
                  <b>{r.curso.titulo}</b> · {n} intento{n === 1 ? '' : 's'}
                </span>
                <span
                  className={`font-extrabold ${r.aprobado ? 'text-verde' : 'text-rojo'}`}
                >
                  {r.aprobado ? '✓ Aprobado' : '✗ Pendiente'} · Nota {r.mejorNota}
                </span>
              </div>
            );
          })}
        </section>
      )}

      {/* ---------- accesos ---------- */}
      <section className="grid gap-4 md:grid-cols-3">
        <Acceso
          a="/modulos"
          ico="📚"
          titulo="Módulos de aprendizaje"
          texto="Cursos cortos por rol y canal: técnicas de venta, portafolio, experiencia del cliente y más."
          pie="Explorar cursos →"
          color="var(--azul)"
        />
        <Acceso
          a="/quizzes"
          ico="📝"
          titulo="Quizzes"
          texto="Evalúa lo aprendido. Cada quiz aprobado suma puntos y te acerca a tu certificación."
          pie="Ver evaluaciones →"
          color="var(--rojo)"
        />
        <Acceso
          a="/recompensas"
          ico="🎁"
          titulo="Recompensas"
          texto="Cada curso aprobado suma puntos. El catálogo de premios de esta campaña se abre pronto."
          pie="Ver mis puntos →"
          color="var(--oro)"
        />
      </section>

      {resumen === null && <p className="py-6 text-center text-gris">Cargando tus cursos…</p>}
    </div>
  );
}

function Kpi({
  valor,
  etiqueta,
  color,
  extra,
}: {
  valor: string | number;
  etiqueta: string;
  color: string;
  extra?: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-5 shadow-[0_10px_30px_rgba(30,63,148,.08)]">
      <span className="absolute inset-x-0 top-0 h-1" style={{ background: color }} />
      <p className="font-titulos text-3xl font-black leading-none">{valor}</p>
      <p className="mt-1.5 text-[0.68rem] font-bold uppercase tracking-wide text-gris">
        {etiqueta}
      </p>
      {extra && <p className="mt-1.5 text-xs font-bold text-verde">{extra}</p>}
    </div>
  );
}

function Acceso({
  a,
  ico,
  titulo,
  texto,
  pie,
  color,
}: {
  a: string;
  ico: string;
  titulo: string;
  texto: string;
  pie: string;
  color: string;
}) {
  return (
    <Link
      to={a}
      className="group relative overflow-hidden rounded-2xl bg-white p-6 text-left shadow-[0_10px_30px_rgba(30,63,148,.08)] transition hover:-translate-y-0.5"
    >
      <span className="absolute inset-x-0 top-0 h-1" style={{ background: color }} />
      <span className="text-3xl leading-none">{ico}</span>
      <h3 className="mt-3 font-titulos text-lg font-extrabold">{titulo}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-gris">{texto}</p>
      <p className="mt-3 text-sm font-extrabold" style={{ color }}>
        {pie}
      </p>
    </Link>
  );
}
